import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import { MEMBER_URLS, PUBLIC_URLS, getResourceUrl } from '../config/apiConfig';
import '../../styles/CommonStyles.css';

function ProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [upcomingPrograms, setUpcomingPrograms] = useState([]);
  const [pastPrograms, setPastPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState({});
  const [showResourcesFor, setShowResourcesFor] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [programResources, setProgramResources] = useState({});
  const [resourcesLoading, setResourcesLoading] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUpcomingPrograms, setFilteredUpcomingPrograms] = useState([]);
  const [filteredPastPrograms, setFilteredPastPrograms] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null); // 'success' or 'failed'
  const [paymentProgramTitle, setPaymentProgramTitle] = useState('');
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);

  const LoadingSpinner = () => (
    <div style={{ textAlign: 'center', padding: '40px' }}>
      <div style={{ 
        border: '4px solid rgba(0,0,0,0.1)', 
        borderRadius: '50%', 
        borderTop: '4px solid var(--primary-color)', 
        width: '50px', 
        height: '50px', 
        margin: '0 auto',
        animation: 'spin 1s linear infinite' 
      }}></div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <p style={{ marginTop: '15px', fontSize: '16px', color: 'var(--primary-color)', fontWeight: '500' }}>Loading programs...</p>
    </div>
  );

  const ErrorAlert = ({ message }) => (
    <div style={{ 
      backgroundColor: '#f8d7da', 
      color: '#721c24', 
      padding: '15px 25px', 
      borderRadius: '8px', 
      margin: '20px 0',
      border: '1px solid #f5c6cb',
      boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
    }}>
      <strong>Error: </strong>{message}
    </div>
  );

  // Fetch all programs (upcoming and past) on component mount
  useEffect(() => {
    const fetchAllPrograms = async () => {
      try {
        setLoading(true);
        
        // Fetch upcoming programs
        const upcomingResponse = await axios.get(`${PUBLIC_URLS.PROGRAMS}?type=upcoming`);
        if (upcomingResponse.data && Array.isArray(upcomingResponse.data.items || upcomingResponse.data.programs)) {
          const upcoming = upcomingResponse.data.items || upcomingResponse.data.programs;
          upcoming.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
          setUpcomingPrograms(upcoming);
        }
        
        // Fetch past programs
        const pastResponse = await axios.get(`${PUBLIC_URLS.PROGRAMS}?type=past`);
        if (pastResponse.data && Array.isArray(pastResponse.data.items || pastResponse.data.programs)) {
          const past = pastResponse.data.items || pastResponse.data.programs;
          past.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
          setPastPrograms(past);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error loading programs:", err);
        setError("Failed to load programs. Please try again later.");
        setLoading(false);
      }
    };
    
    fetchAllPrograms();
  }, []);

  // Login Modal Component - Extend to include non-member registration
  const LoginModal = ({ onClose, programId }) => {
    const [registrationType, setRegistrationType] = useState('member');
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      phone: '',
      city: '',
      ibbiNumber: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Clear error when typing
      if (formErrors[name]) {
        setFormErrors({
          ...formErrors,
          [name]: null
        });
      }
    };
    
    const validateForm = () => {
      const errors = {};
      
      if (!formData.name.trim()) errors.name = "Name is required";
      if (!formData.email.trim()) {
        errors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = "Please enter a valid email";
      }
      if (!formData.phone.trim()) errors.phone = "Phone number is required";
      if (!formData.city.trim()) errors.city = "City is required";
      
      return errors;
    };
    
    const handleNonMemberRegistration = async () => {
      const errors = validateForm();
      setFormErrors(errors);
      
      if (Object.keys(errors).length === 0) {
        setIsSubmitting(true);
        
        try {
          // Find the program details to get price information
          const programToRegister = [...upcomingPrograms, ...pastPrograms].find(p => p.id === programId);
          if (!programToRegister) {
            throw new Error("Program not found");
          }
          
          const registrationData = {
            ...formData,
            price: programToRegister.non_member_price
          };
          
          const response = await axios.post(
            PUBLIC_URLS.PROGRAM_REGISTRATION(programId),
            registrationData
          );
          
          // Check if user is already registered
          if (response.data.message === "You are already registered for this program" && response.data.success === true) {
            setRegistrationStatus({...registrationStatus, [programId]: 'already-registered'});
            onClose();
            return;
          }
          
          if (response.data.payment) {
            // Handle payment for paid programs
            const paymentData = response.data.payment;
            
            // Load Razorpay script if not already loaded
            if (!window.Razorpay) {
              await new Promise((resolve) => {
                const script = document.createElement('script');
                script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                script.onload = resolve;
                document.body.appendChild(script);
              });
            }
            
            // Create Razorpay payment instance
            const options = {
              key: paymentData.razorpay_key_id,
              amount: paymentData.amount,
              currency: paymentData.currency,
              name: "Bangalore Valuers Association",
              description: `Registration for ${programToRegister.title}`,
              order_id: paymentData.razorpay_order_id,
              handler: async function(response) {
                try {
                  // Show loading state while verifying payment
                  setIsVerifyingPayment(true);
                  
                  // Verify payment and complete registration
                  const verificationResponse = await axios.post(
                    PUBLIC_URLS.PAYMENT_VERIFICATION,
                    {
                      razorpay_payment_id: response.razorpay_payment_id,
                      razorpay_order_id: response.razorpay_order_id,
                      razorpay_signature: response.razorpay_signature,
                      payment_id: paymentData.payment_id,
                      is_member: false,
                      registrant_email: formData.email
                    }
                  );
                  
                  if (verificationResponse.data.success) {
                    setRegistrationStatus({...registrationStatus, [programId]: 'success'});
                    setPaymentResult('success');
                    setPaymentProgramTitle(programToRegister.title);
                    setShowPaymentModal(true);
                    onClose();
                  } else {
                    setRegistrationStatus({...registrationStatus, [programId]: 'error'});
                    setPaymentResult('failed');
                    setPaymentProgramTitle(programToRegister.title);
                    setShowPaymentModal(true);
                  }
                } catch (err) {
                  console.error("Payment verification error:", err);
                  setRegistrationStatus({...registrationStatus, [programId]: 'error'});
                  setPaymentResult('failed');
                  setPaymentProgramTitle(programToRegister.title);
                  setShowPaymentModal(true);
                } finally {
                  setIsSubmitting(false);
                  setIsVerifyingPayment(false);
                }
              },
              prefill: {
                name: formData.name,
                email: formData.email,
                contact: formData.phone
              },
              theme: {
                color: "#0063B2",
              },
              modal: {
                ondismiss: function() {
                  setIsSubmitting(false);
                }
              }
            };
            
            const razorpay = new window.Razorpay(options);
            razorpay.open();
          } else {
            // Handle free program registration
            setRegistrationStatus({...registrationStatus, [programId]: 'success'});
            onClose();
          }
        } catch (err) {
          console.error("Non-member registration error:", err);
          // Check if the error response contains the already registered message
          if (err.response?.data?.message === "You are already registered for this program" && 
              err.response?.data?.success === true) {
            setRegistrationStatus({...registrationStatus, [programId]: 'already-registered'});
            onClose();
          } else {
            setFormErrors({
              ...formErrors,
              server: err.response?.data?.error || "Registration failed. Please try again."
            });
          }
        } finally {
          setIsSubmitting(false);
        }
      }
    };
    
    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="modal-content" style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '30px',
          width: '90%',
          maxWidth: '500px',
          position: 'relative',
          boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
          animation: 'fadeInUp 0.3s ease',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}>
          <style>{`
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            .input-error {
              border-color: #dc3545 !important;
            }
            .error-text {
              color: #dc3545;
              font-size: 12px;
              margin-top: 5px;
            }
          `}</style>
          
          <button 
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'none',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              color: '#555'
            }}
          >
            &times;
          </button>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: 'rgba(var(--primary-rgb), 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 15px'
            }}>
              <i className={registrationType === 'member' ? "fas fa-user-lock" : "fas fa-user-plus"} 
                style={{ fontSize: '24px', color: 'var(--primary-color)' }}></i>
            </div>
            <h3 style={{ fontSize: '20px', marginBottom: '8px', color: '#333' }}>
              {registrationType === 'member' ? 'Member Login' : 'Non-Member Registration'}
            </h3>
            <p style={{ color: '#666', fontSize: '15px', lineHeight: '1.5' }}>
              {registrationType === 'member' 
                ? 'Please log in to register for this program. BVA members enjoy special benefits for all programs.' 
                : 'Please provide your information to register for this program.'}
            </p>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '20px'
          }}>
            <button
              onClick={() => setRegistrationType('member')}
              style={{
                backgroundColor: registrationType === 'member' ? 'var(--primary-color)' : '#f8f9fa',
                color: registrationType === 'member' ? 'white' : '#495057',
                border: '1px solid ' + (registrationType === 'member' ? 'var(--primary-color)' : '#dee2e6'),
                borderRadius: '20px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <i className="fas fa-user-tie" style={{ marginRight: '5px' }}></i>
              Member
            </button>
            <button
              onClick={() => setRegistrationType('nonMember')}
              style={{
                backgroundColor: registrationType === 'nonMember' ? 'var(--primary-color)' : '#f8f9fa',
                color: registrationType === 'nonMember' ? 'white' : '#495057',
                border: '1px solid ' + (registrationType === 'nonMember' ? 'var(--primary-color)' : '#dee2e6'),
                borderRadius: '20px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <i className="fas fa-user" style={{ marginRight: '5px' }}></i>
              Non-Member
            </button>
          </div>
          
          {registrationType === 'member' ? (
            <div style={{ display: 'flex', gap: '15px', marginTop: '25px' }}>
              <button
                onClick={() => redirectToLogin(programId)}
                style={{
                  flex: 1,
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-dark-color, #0056b3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-color)';
                }}
              >
                Login to Continue
              </button>
              <button 
                onClick={onClose}
                style={{
                  flex: 1,
                  backgroundColor: '#f8f9fa',
                  color: '#495057',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e9ecef';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ marginTop: '20px' }}>
              {formErrors.server && (
                <div style={{
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  padding: '10px',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  fontSize: '14px'
                }}>
                  <i className="fas fa-exclamation-circle" style={{ marginRight: '5px' }}></i>
                  {formErrors.server}
                </div>
              )}
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                  Full Name*
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid ' + (formErrors.name ? '#dc3545' : '#ced4da'),
                    borderRadius: '4px',
                  }}
                  className={formErrors.name ? 'input-error' : ''}
                />
                {formErrors.name && <div className="error-text">{formErrors.name}</div>}
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                  Email Address*
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid ' + (formErrors.email ? '#dc3545' : '#ced4da'),
                    borderRadius: '4px'
                  }}
                  className={formErrors.email ? 'input-error' : ''}
                />
                {formErrors.email && <div className="error-text">{formErrors.email}</div>}
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                  Phone Number*
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid ' + (formErrors.phone ? '#dc3545' : '#ced4da'),
                    borderRadius: '4px'
                  }}
                  className={formErrors.phone ? 'input-error' : ''}
                />
                {formErrors.phone && <div className="error-text">{formErrors.phone}</div>}
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                  City*
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter your city"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid ' + (formErrors.city ? '#dc3545' : '#ced4da'),
                    borderRadius: '4px'
                  }}
                  className={formErrors.city ? 'input-error' : ''}
                />
                {formErrors.city && <div className="error-text">{formErrors.city}</div>}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                  IBBI Registration Number (if applicable)
                </label>
                <input
                  type="text"
                  name="ibbiNumber"
                  value={formData.ibbiNumber}
                  onChange={handleInputChange}
                  placeholder="Enter your IBBI registration number"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '15px', marginTop: '15px' }}>
                <button
                  onClick={handleNonMemberRegistration}
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                    opacity: isSubmitting ? 0.7 : 1,
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) e.target.style.backgroundColor = 'var(--primary-dark-color, #0056b3)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) e.target.style.backgroundColor = 'var(--primary-color)';
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <span style={{ visibility: 'hidden' }}>Register Now</span>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <div style={{ 
                          border: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}></div>
                        Processing...
                      </div>
                    </>
                  ) : 'Register Now'}
                </button>
                <button 
                  onClick={onClose}
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '12px 15px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) e.target.style.backgroundColor = '#e9ecef';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) e.target.style.backgroundColor = '#f8f9fa';
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Payment Result Modal Component
  const PaymentResultModal = () => {
    const handleClose = () => {
      setShowPaymentModal(false);
      setPaymentResult(null);
      setPaymentProgramTitle('');
    };

    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="modal-content" style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '40px',
          width: '90%',
          maxWidth: '500px',
          position: 'relative',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          animation: 'fadeInUp 0.3s ease',
          textAlign: 'center'
        }}>
          <style>{`
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          
          <button 
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'none',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              color: '#555',
              padding: '5px'
            }}
          >
            &times;
          </button>
          
          {paymentResult === 'success' ? (
            <div>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <i className="fas fa-check-circle" style={{ 
                  fontSize: '40px', 
                  color: '#28a745' 
                }}></i>
              </div>
              
              <h3 style={{ 
                fontSize: '24px', 
                marginBottom: '15px', 
                color: '#28a745',
                fontWeight: '600'
              }}>
                Payment Successful!
              </h3>
              
              <p style={{ 
                color: '#666', 
                fontSize: '16px', 
                lineHeight: '1.5',
                marginBottom: '15px'
              }}>
                Your registration for <strong>{paymentProgramTitle}</strong> has been confirmed.
              </p>
              
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                margin: '20px 0',
                border: '1px solid #e9ecef'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '10px'
                }}>
                  <i className="fas fa-envelope" style={{ 
                    color: 'var(--primary-color)', 
                    marginRight: '8px',
                    fontSize: '18px'
                  }}></i>
                  <span style={{ 
                    fontWeight: '600',
                    color: '#333',
                    fontSize: '16px'
                  }}>Confirmation Email Sent</span>
                </div>
                <p style={{
                  color: '#666',
                  fontSize: '14px',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  Program details and joining instructions have been sent to your registered email address.
                </p>
              </div>
              
              <button
                onClick={handleClose}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  marginTop: '10px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#218838';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#28a745';
                }}
              >
                Got it, Thanks!
              </button>
            </div>
          ) : (
            <div>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <i className="fas fa-times-circle" style={{ 
                  fontSize: '40px', 
                  color: '#dc3545' 
                }}></i>
              </div>
              
              <h3 style={{ 
                fontSize: '24px', 
                marginBottom: '15px', 
                color: '#dc3545',
                fontWeight: '600'
              }}>
                Payment Failed
              </h3>
              
              <p style={{ 
                color: '#666', 
                fontSize: '16px', 
                lineHeight: '1.5',
                marginBottom: '20px'
              }}>
                Unfortunately, your payment for <strong>{paymentProgramTitle}</strong> could not be processed.
              </p>
              
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                margin: '20px 0',
                border: '1px solid #e9ecef'
              }}>
                <p style={{
                  color: '#666',
                  fontSize: '14px',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  <strong>What to do next:</strong><br/>
                  • Check your payment method and try again<br/>
                  • Contact your bank if the issue persists<br/>
                  • Reach out to us if you need assistance
                </p>
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: '15px', 
                justifyContent: 'center',
                marginTop: '20px'
              }}>
                <button
                  onClick={handleClose}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 25px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--primary-dark-color, #0056b3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--primary-color)';
                  }}
                >
                  Try Again
                </button>
                <button
                  onClick={handleClose}
                  style={{
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    padding: '12px 25px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e9ecef';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Helper function to find program title by ID
  const getProgramTitle = (programId) => {
    const program = [...upcomingPrograms, ...pastPrograms].find(p => p.id === programId);
    return program ? program.title : 'Program';
  };

  const handleRegistration = async (programId) => {
    try {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      
      if (!token) {
        // Show login modal instead of direct redirect
        setSelectedProgramId(programId);
        setShowLoginModal(true);
        return;
      }
      
      setRegistrationStatus({...registrationStatus, [programId]: 'loading'});
      
      // Find the program details to get price information
      const programToRegister = [...upcomingPrograms, ...pastPrograms].find(p => p.id === programId);
      if (!programToRegister) {
        throw new Error("Program not found");
      }
      
      // Check if the program requires payment
      if (programToRegister.is_paid) {
        // Use member_price when logged in
        const priceToCharge = isLoggedIn ? programToRegister.member_price : programToRegister.non_member_price;
        
        // Create payment request for the program
        const paymentResponse = await axios.post(
          MEMBER_URLS.PROGRAM_REGISTRATION(programId),
          { price: priceToCharge }, // Pass the appropriate price to the backend
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Check if user is already registered
        if (paymentResponse.data.message === "You are already registered for this program" && paymentResponse.data.success === true) {
          setRegistrationStatus({...registrationStatus, [programId]: 'already-registered'});
          return;
        }
        
        // If payment is required, handle the razorpay payment
        if (paymentResponse.data.payment) {
          const paymentData = paymentResponse.data.payment;
          
          // Load Razorpay script if not already loaded
          if (!window.Razorpay) {
            await new Promise((resolve) => {
              const script = document.createElement('script');
              script.src = 'https://checkout.razorpay.com/v1/checkout.js';
              script.onload = resolve;
              document.body.appendChild(script);
            });
          }
          
          // Create Razorpay payment instance
          const options = {
            key: paymentData.razorpay_key_id,
            amount: paymentData.amount,
            currency: paymentData.currency,
            name: "Bangalore Valuers Association",
            description: `Registration for ${programToRegister.title}`,
            order_id: paymentData.razorpay_order_id,
            handler: async function(response) {
              try {
                // Show loading state while verifying payment
                setIsVerifyingPayment(true);
                
                // Verify payment and complete registration
                const verificationResponse = await axios.post(
                  PUBLIC_URLS.PAYMENT_VERIFICATION,
                  {
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_signature: response.razorpay_signature,
                    payment_id: paymentData.payment_id,
                  },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                
                if (verificationResponse.data.success) {
                  setRegistrationStatus({...registrationStatus, [programId]: 'success'});
                  setPaymentResult('success');
                  setPaymentProgramTitle(programToRegister.title);
                  setShowPaymentModal(true);
                } else {
                  setRegistrationStatus({...registrationStatus, [programId]: 'error'});
                  setPaymentResult('failed');
                  setPaymentProgramTitle(programToRegister.title);
                  setShowPaymentModal(true);
                }
              } catch (err) {
                console.error("Payment verification error:", err);
                setRegistrationStatus({...registrationStatus, [programId]: 'error'});
                setPaymentResult('failed');
                setPaymentProgramTitle(programToRegister.title);
                setShowPaymentModal(true);
              } finally {
                setIsVerifyingPayment(false);
              }
            },
            prefill: {
              name: localStorage.getItem('userName') || '',
              email: localStorage.getItem('userEmail') || '',
            },
            theme: {
              color: "#0063B2",
            },
            modal: {
              ondismiss: function() {
                setRegistrationStatus({...registrationStatus, [programId]: null});
              }
            }
          };
          
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        } else {
          // Free registration or payment not required
          setRegistrationStatus({...registrationStatus, [programId]: 'success'});
        }
      } else {
        // For free programs, just register directly
        const response = await axios.post(
          PUBLIC_URLS.PROGRAM_REGISTRATION(programId),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Check if user is already registered
        if (response.data.message === "You are already registered for this program" && response.data.success === true) {
          setRegistrationStatus({...registrationStatus, [programId]: 'already-registered'});
          return;
        }
        
        if (response.data.success) {
          setRegistrationStatus({...registrationStatus, [programId]: 'success'});
        } else {
          setRegistrationStatus({...registrationStatus, [programId]: 'error'});
        }
      }
    } catch (err) {
      console.error("Registration error:", err);
      // Check if the error response contains the already registered message
      if (err.response?.data?.message === "You are already registered for this program" && err.response?.data?.success === true) {
        setRegistrationStatus({...registrationStatus, [programId]: 'already-registered'});
      } else {
        setRegistrationStatus({...registrationStatus, [programId]: 'error'});
      }
    }
  };
  
  const fetchProgramResources = async (programId) => {
    if (programResources[programId] !== undefined) {
      return; // Already fetched or attempted
    }

    try {
      setResourcesLoading(prev => ({ ...prev, [programId]: true }));
      
      // Get authentication token if available
      const token = localStorage.getItem('token');
      const headers = {};
      
      // Add authorization header if token is available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch program-specific resources using the backend endpoint
      const response = await axios.get(`${PUBLIC_URLS.RESOURCES}?program_id=${programId}`, { headers });
      
      // Handle the response format from backend (should be {items: []})
      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        setProgramResources(prev => ({ ...prev, [programId]: response.data.items }));
        
        // Update status based on if user is authenticated (has access to member-only resources)
        setIsLoggedIn(response.data.is_authenticated || isLoggedIn);
      } else {
        // No program-specific resources found
        setProgramResources(prev => ({ ...prev, [programId]: [] }));
      }
    } catch (err) {
      console.error('Error fetching program resources:', err);
      setProgramResources(prev => ({ ...prev, [programId]: [] }));
    } finally {
      setResourcesLoading(prev => ({ ...prev, [programId]: false }));
    }
  };

  const toggleResourcesView = async (programId) => {
    if (showResourcesFor === programId) {
      setShowResourcesFor(null);
    } else {
      setShowResourcesFor(programId);
      // Fetch resources when showing for the first time
      if (programResources[programId] === undefined) {
        await fetchProgramResources(programId);
      }
    }
  };

  // Format date and time for program display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date)) return dateString;
      
      // Format like "15th January 2023"
      const day = date.getDate();
      const month = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      
      // Add ordinal suffix to day
      let suffix = 'th';
      if (day === 1 || day === 21 || day === 31) suffix = 'st';
      else if (day === 2 || day === 22) suffix = 'nd';
      else if (day === 3 || day === 23) suffix = 'rd';
      
      return `${day}${suffix} ${month} ${year}`;
    } catch (e) {
      return dateString;
    }
  };

  // Format date and time together for program display
  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date)) return dateString;
      
      // Format date
      const day = date.getDate();
      const month = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      
      // Add ordinal suffix to day
      let suffix = 'th';
      if (day === 1 || day === 21 || day === 31) suffix = 'st';
      else if (day === 2 || day === 22) suffix = 'nd';
      else if (day === 3 || day === 23) suffix = 'rd';
      
      // Format time
      const time = date.toLocaleString('default', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      
      return `${day}${suffix} ${month} ${year} at ${time}`;
    } catch (e) {
      return dateString;
    }
  };

  useEffect(() => {
    // Filter programs whenever search query changes or programs change
    const filterPrograms = () => {
      const query = searchQuery.toLowerCase().trim();
      
      if (query === '') {
        // If no search query, show all programs
        setFilteredUpcomingPrograms(upcomingPrograms);
        setFilteredPastPrograms(pastPrograms);
        return;
      }

      // Filter upcoming programs
      const filteredUpcoming = upcomingPrograms.filter(program => 
        (program.title && program.title.toLowerCase().includes(query)) || 
        (program.speaker_name && program.speaker_name.toLowerCase().includes(query))
      );
      setFilteredUpcomingPrograms(filteredUpcoming);
      
      // Filter past programs
      const filteredPast = pastPrograms.filter(program => 
        (program.title && program.title.toLowerCase().includes(query)) || 
        (program.speaker_name && program.speaker_name.toLowerCase().includes(query))
      );
      setFilteredPastPrograms(filteredPast);
    };
    
    filterPrograms();
  }, [searchQuery, upcomingPrograms, pastPrograms]);

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  // Clear search query
  const clearSearch = () => {
    setSearchQuery('');
  };

  const renderUpcomingPrograms = () => {
    if (searchQuery && filteredUpcomingPrograms.length === 0) {
      // Skip section if no results in search
      return null;
    }
    
    if (filteredUpcomingPrograms.length === 0) {
      return (
        <div className="empty-state" style={{
          padding: '40px 20px',
          textAlign: 'center',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          margin: '20px 0 40px'
        }}>
          <img 
            src="/images/calendar-icon.svg" 
            alt="Calendar" 
            style={{ 
              width: '60px',
              height: '60px',
              marginBottom: '15px',
              opacity: '0.5'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <p style={{ fontSize: '18px', color: '#666' }}>
            No upcoming programs available at the moment.
          </p>
        </div>
      );
    }
    
    return (
      <div className="card-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
        gap: '25px',
        marginTop: '30px',
        marginBottom: '50px'
      }}>
        {filteredUpcomingPrograms.map(program => (
          <div className="card" key={program.id} style={{
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s, box-shadow 0.3s',
            backgroundColor: '#fff',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)';
          }}
          >
            <div 
              className="card-image" 
              style={{ 
                backgroundImage: program.image_url 
                  ? `url(${program.image_url})` 
                  : "url(https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80)",
                height: '180px',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            ></div>
            <div className="card-content" style={{
              padding: '20px',
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h3 style={{
                fontSize: '20px',
                marginBottom: '15px',
                color: '#333',
                lineHeight: '1.3'
              }}>{program.title}</h3>
              <h3 style={{
                fontSize: '15px',
                marginBottom: '15px',
                color: '#333',
                lineHeight: '1.3'
              }}>{program.speaker_name}</h3>
              
              {/* Program Dates - Enhanced Display */}
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '15px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <i className="fas fa-calendar-alt" style={{ 
                    color: 'var(--primary-color)', 
                    marginRight: '8px',
                    fontSize: '16px'
                  }}></i>
                  <span style={{ 
                    fontWeight: '600',
                    color: '#333',
                    fontSize: '14px'
                  }}>Program Schedule</span>
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px'
                  }}>
                    <span style={{ 
                      color: '#666',
                      minWidth: '60px',
                      fontWeight: '500'
                    }}>Start:</span>
                    <span style={{ 
                      color: '#333',
                      fontWeight: '600'
                    }}>{formatDateTime(program.program_date)}</span>
                  </div>
                  {program.end_date && program.end_date !== program.program_date && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px'
                    }}>
                      <span style={{ 
                        color: '#666',
                        minWidth: '60px',
                        fontWeight: '500'
                      }}>End:</span>
                      <span style={{ 
                        color: '#333',
                        fontWeight: '600'
                      }}>{formatDateTime(program.end_date)}</span>
                    </div>
                  )}
                  {(!program.end_date || program.end_date === program.program_date) && (
                    <div style={{
                      fontSize: '13px',
                      color: '#666',
                      fontStyle: 'italic'
                    }}>Single session program</div>
                  )}
                </div>
              </div>
              
              <div style={{ 
                marginBottom: '12px',
                color: '#555'
              }}>
                
                {/* Display price information */}
                {program.is_paid && (
                  <div style={{ 
                    backgroundColor: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <i className="fas fa-tags" style={{ 
                        color: 'var(--primary-color)', 
                        marginRight: '8px',
                        fontSize: '16px'
                      }}></i>
                      <span style={{ 
                        fontWeight: '600',
                        color: '#333',
                        fontSize: '14px'
                      }}>Program Fees</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '14px'
                      }}>
                        <span style={{ 
                          color: '#666',
                          fontWeight: '500'
                        }}>Fee for BVA Members</span>
                        <span style={{ 
                          color: '#198754',
                          fontWeight: '600'
                        }}>{formatPrice(program.member_price)}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '14px'
                      }}>
                        <span style={{ 
                          color: '#666',
                          fontWeight: '500'
                        }}>Fee for Non Members</span>
                        <span style={{ 
                          color: 'var(--primary-color)',
                          fontWeight: '600'
                        }}>{formatPrice(program.non_member_price)}</span>
                      </div>
                      {isLoggedIn && program.member_price !== program.non_member_price && (
                        <div style={{
                          fontSize: '12px',
                          color: '#198754',
                          fontWeight: '500',
                          marginTop: '4px',
                          textAlign: 'center',
                          backgroundColor: 'rgba(25, 135, 84, 0.1)',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}>
                          <i className="fas fa-star" style={{ marginRight: '4px' }}></i>
                          You save {formatPrice(program.non_member_price - program.member_price)} as a member!
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <p style={{
                display: 'flex', 
                alignItems: 'center',
                marginBottom: '15px',
                color: '#555',
                fontSize: '14px'
              }}>
                <i className="fas fa-map-marker-alt" style={{ marginRight: '8px', color: 'var(--primary-color)' }}></i>
                <span>{program.location || "TBA"}</span>
              </p>
              
              <p className="program-description" style={{
                color: '#666',
                marginBottom: '20px',
                lineHeight: '1.5',
                display: '-webkit-box',
                WebkitLineClamp: '3',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1
              }}>{program.description}</p>
              
              <div className="card-actions" style={{ marginTop: 'auto' }}>
                {registrationStatus[program.id] === 'success' ? (
                  <div className="registration-success" style={{
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    padding: '10px 15px',
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    <i className="fas fa-check-circle" style={{ marginRight: '5px' }}></i>
                    Successfully registered!
                  </div>
                ) : registrationStatus[program.id] === 'already-registered' ? (
                  <div className="registration-already" style={{
                    backgroundColor: '#fffbeb',
                    color: '#92400e',
                    padding: '10px 15px',
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontSize: '14px',
                    border: '1px solid #fef3c7'
                  }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '5px' }}></i>
                    You're already registered for this program
                  </div>
                ) : (
                  <button 
                    className={`btn btn-primary ${registrationStatus[program.id] === 'loading' ? 'loading' : ''}`}
                    onClick={() => handleRegistration(program.id)}
                    disabled={registrationStatus[program.id] === 'loading'}
                    style={{
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      width: '100%',
                      transition: 'background-color 0.2s',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--primary-dark-color, #0056b3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--primary-color)';
                    }}
                  >
                    {registrationStatus[program.id] === 'loading' ? (
                      <>
                        <span style={{ opacity: 0 }}>
                          {program.is_paid ? 'REGISTER NOW' : 'REGISTER NOW'}
                        </span>
                        <span style={{ 
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <div style={{ 
                            border: '2px solid white',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            width: '18px',
                            height: '18px',
                            animation: 'spin 1s linear infinite',
                            marginRight: '8px'
                          }}></div>
                          {program.is_paid ? 'Processing...' : 'Registering...'}
                        </span>
                      </>
                    ) : (
                      <>REGISTER NOW</>
                    )}
                  </button>
                )}
                {/* Show member price info if not logged in and there's a price difference */}
                {!isLoggedIn && program.is_paid && program.member_price !== undefined && 
                 program.member_price !== program.non_member_price && (
                  <div style={{
                    fontSize: '13px',
                    textAlign: 'center',
                    marginTop: '8px',
                    color: '#666'
                  }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                    Members save {formatPrice(program.non_member_price - program.member_price)} on this program
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPastPrograms = () => {
    if (searchQuery && filteredPastPrograms.length === 0) {
      // Skip section if no results in search
      return null;
    }
    
    if (filteredPastPrograms.length === 0) {
      return (
        <div className="empty-state" style={{
          padding: '40px 20px',
          textAlign: 'center',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          <i className="fas fa-history" style={{ 
            fontSize: '48px',
            color: '#004a94',
            opacity: '0.5',
            marginBottom: '15px'
          }}></i>
          <p style={{ fontSize: '18px', color: '#666' }}>
            No past programs available at the moment.
          </p>
        </div>
      );
    }
    
    return (
      <div className="card-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
        gap: '25px',
        marginTop: '30px' 
      }}>
        {filteredPastPrograms.map(program => (
          <div className="card" key={program.id} style={{
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s, box-shadow 0.3s',
            backgroundColor: '#fff',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e9ecef'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)';
          }}
          >
            {/* Enhanced date banner with better start/end date display */}
            <div className="card-date-banner" style={{ 
                backgroundColor: '#616161', // Darker color for past programs
                color: 'white', 
                padding: '15px 20px',
                position: 'relative',
                overflow: 'hidden'
              }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '80px',
                height: '80px',
                background: '#ffc107',
                clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
                opacity: '0.2'
              }}></div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '5px'
                  }}>
                    <i className="fas fa-calendar-check" style={{ 
                      marginRight: '8px',
                      fontSize: '16px'
                    }}></i>
                    <span style={{ 
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: '0.9'
                    }}>Program Schedule</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{ 
                        fontSize: '16px',
                        fontWeight: '600'
                      }}>
                        {formatDate(program.program_date)}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: '500',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  minWidth: 'fit-content'
                }}>
                  <i className="fas fa-map-marker-alt" style={{ marginRight: '5px' }}></i>
                  {program.location || "TBA"}
                </div>
              </div>
            </div>
            
            <div className="card-content" style={{
              padding: '20px',
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h3 style={{
                fontSize: '20px',
                marginBottom: '10px',
                color: '#004a94',
                lineHeight: '1.3'
              }}>{program.title}</h3>
              
              {program.speaker_name && (
                <p style={{
                  color: '#555',
                  marginBottom: '15px',
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <i className="fas fa-user-tie" style={{ 
                    color: '#ffc107',
                    marginRight: '8px'
                  }}></i>
                  <span>{program.speaker_name}</span>
                </p>
              )}
              
              <p className="program-description" style={{
                color: '#666',
                marginBottom: '20px',
                lineHeight: '1.5',
                display: '-webkit-box',
                WebkitLineClamp: '3',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1
              }}>{program.description}</p>
              
              <div className="card-actions" style={{ marginTop: 'auto' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => toggleResourcesView(program.id)}
                  style={{
                    backgroundColor: showResourcesFor === program.id ? '#e9ecef' : 'transparent',
                    color: '#004a94',
                    border: '2px solid #004a94',
                    borderRadius: '6px',
                    padding: '10px 18px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (showResourcesFor !== program.id) {
                      e.target.style.backgroundColor = 'rgba(0, 74, 148, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (showResourcesFor !== program.id) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {showResourcesFor === program.id ? (
                    <><i className="fas fa-chevron-up" style={{marginRight: '8px'}}></i>HIDE RESOURCES</>
                  ) : (
                    <>
                      <i className="fas fa-file-alt" style={{marginRight: '8px'}}></i>
                      VIEW RESOURCES
                    </>
                  )}
                </button>
              </div>
              
              {showResourcesFor === program.id && (
                <div className="program-resources" style={{
                  marginTop: '15px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  animation: 'fadeIn 0.3s'
                }}>
                  <style>{`
                    @keyframes fadeIn {
                      from { opacity: 0; transform: translateY(-10px); }
                      to { opacity: 1; transform: translateY(0); }
                    }
                  `}</style>
                  
                  <h4 style={{
                    fontSize: '16px',
                    marginBottom: '15px',
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span>
                      <i className="fas fa-folder-open" style={{marginRight: '8px', color: '#004a94'}}></i>
                      Program Resources
                    </span>
                  </h4>

                  {resourcesLoading[program.id] ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#6c757d'
                    }}>
                      <i className="fas fa-spinner fa-spin" style={{marginRight: '8px'}}></i>
                      Loading resources...
                    </div>
                  ) : (
                    <>
                      {programResources[program.id] && programResources[program.id].length > 0 ? (
                        <ul className="resource-list" style={{
                          listStyleType: 'none',
                          padding: 0,
                          margin: 0
                        }}>
                          {programResources[program.id].map((resource, index) => (
                            <li key={resource.id || index} style={{
                              marginBottom: '8px',
                              padding: '8px 10px',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              border: '1px solid #dee2e6',
                              transition: 'transform 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateX(5px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}>
                              <a 
                                href={getResourceUrl(resource.file_url || resource.url)} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{
                                  color: '#004a94',
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <i className={`fas ${getFileIcon(resource.file_type || resource.resource_type)}`} style={{marginRight: '8px'}}></i>
                                {resource.title || resource.name || 'Resource'} 
                                <span style={{
                                  fontSize: '12px',
                                  backgroundColor: '#e9ecef',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  marginLeft: '8px'
                                }}>
                                  {resource.file_type || resource.resource_type || 'File'}
                                </span>
                                {!resource.is_public && (
                                  <span style={{
                                    fontSize: '11px',
                                    backgroundColor: '#ffcc00',
                                    color: '#333',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    marginLeft: '8px',
                                    fontWeight: 'bold'
                                  }}>
                                    MEMBERS ONLY
                                  </span>
                                )}
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div style={{
                          padding: '20px',
                          backgroundColor: '#fff',
                          border: '1px dashed #dee2e6',
                          borderRadius: '6px',
                          textAlign: 'center'
                        }}>
                          {!isLoggedIn ? (
                            <>
                              <i className="fas fa-lock" style={{
                                fontSize: '32px',
                                color: '#ffc107',
                                marginBottom: '15px'
                              }}></i>
                              <h5 style={{
                                color: '#333',
                                fontSize: '16px',
                                marginBottom: '8px',
                                fontWeight: '600'
                              }}>
                                Session Videos & PPTs - Members Only
                              </h5>
                              <p style={{
                                color: '#6c757d',
                                fontSize: '14px',
                                marginBottom: '15px',
                                lineHeight: '1.5'
                              }}>
                                Access exclusive session recordings, presentations, and downloadable materials. 
                                Join BVA to unlock our complete resource library.
                              </p>
                              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <button 
                                  onClick={() => redirectToLogin(program.id)}
                                  style={{
                                    backgroundColor: 'var(--primary-color)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '10px 16px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = 'var(--primary-dark-color, #0056b3)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'var(--primary-color)';
                                  }}
                                >
                                  <i className="fas fa-sign-in-alt" style={{marginRight: '6px'}}></i>
                                  Login Now
                                </button>
                                <a 
                                  href="/membership"
                                  style={{
                                    backgroundColor: '#ffc107',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '10px 16px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#e6ac00';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#ffc107';
                                  }}
                                >
                                  <i className="fas fa-star" style={{marginRight: '6px'}}></i>
                                  Become Member
                                </a>
                              </div>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-info-circle" style={{
                                fontSize: '24px',
                                color: '#6c757d',
                                marginBottom: '10px'
                              }}></i>
                              <p style={{
                                color: '#6c757d',
                                fontSize: '14px',
                                marginBottom: '15px'
                              }}>
                                No specific resources available for this program.
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Helper to get appropriate icon for file type
  const getFileIcon = (fileType) => {
    if (!fileType) return 'fa-file';
    
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return 'fa-file-pdf';
    if (type.includes('doc') || type.includes('word')) return 'fa-file-word';
    if (type.includes('xls') || type.includes('excel')) return 'fa-file-excel';
    if (type.includes('ppt') || type.includes('powerpoint')) return 'fa-file-powerpoint';
    if (type.includes('jpg') || type.includes('jpeg') || type.includes('png') || type.includes('image')) return 'fa-file-image';
    if (type.includes('zip') || type.includes('rar')) return 'fa-file-archive';
    
    return 'fa-file';
  };

  // Add useEffect to check login status on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Helper function to redirect to login
  const redirectToLogin = (programId) => {
    localStorage.setItem('redirectAfterLogin', `/programs?register=${programId}`);
    window.location.href = '/member-login';
  };

  // Format price to display currency properly
  const formatPrice = (price) => {
    if (!price && price !== 0) return 'Free';
    
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="page-container">
      {/* Payment Verification Loading Overlay */}
      {isVerifyingPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: 'white',
            fontSize: '1.2rem',
            marginTop: '1.5rem',
            fontWeight: '500'
          }}>
            Verifying your payment...
          </p>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }}>
            Please wait, this may take a few moments
          </p>
        </div>
      )}
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <Header />
      
      <div className="page-header" style={{
        background: 'linear-gradient(135deg, var(--primary-color) 0%, var(--dark-blue-bg, #00366c) 100%)',
        padding: '60px 20px',
        textAlign: 'center',
        color: 'white',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ 
          fontSize: '36px', 
          fontWeight: '700',
          marginBottom: '15px',
          textShadow: '1px 1px 3px rgba(0,0,0,0.2)'
        }}>OUR PROGRAMS</h1>
        <p style={{ 
          fontSize: '18px',
          maxWidth: '800px',
          margin: '0 auto',
          lineHeight: '1.6'
        }}>Explore our educational programs and workshops designed to enhance your valuation expertise</p>
        
        {/* Member-only content notice */}
        {!isLoggedIn && (
          <div style={{
            marginTop: '20px',
            padding: '15px 25px',
            backgroundColor: 'rgba(255, 204, 0, 0.9)',
            borderRadius: '8px',
            border: '1px solid #ffcc00',
            maxWidth: '800px',
            margin: '20px auto 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <i className="fas fa-star" style={{ fontSize: '20px', color: '#333' }}></i>
            <div style={{ textAlign: 'left' }}>
              <p style={{ 
                margin: 0, 
                fontSize: '16px', 
                fontWeight: '600',
                color: '#333'
              }}>
                <strong>Videos & PPTs Available for Members Only</strong>
              </p>
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: '14px',
                color: '#333'
              }}>
                Join BVA to access exclusive session recordings, presentations, and resources
              </p>
            </div>
          </div>
        )}
      </div>
      
      <section className="content-section" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px 20px' 
      }}>
        {error && <ErrorAlert message={error} />}
        
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {/* Search Bar */}
            <div className="search-container" style={{
              marginBottom: '30px',
              position: 'relative',
              maxWidth: '600px',
              margin: '0 auto 30px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                backgroundColor: 'white'
              }}>
                <span style={{
                  padding: '0 15px',
                  color: '#6c757d',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  placeholder="Search programs by title or speaker..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  style={{
                    flex: '1',
                    padding: '14px 15px 14px 0',
                    border: 'none',
                    outline: 'none',
                    fontSize: '16px'
                  }}
                />
                {searchQuery && (
                  <button 
                    onClick={clearSearch}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '0 15px',
                      color: '#6c757d',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              {searchQuery && (
                <div style={{
                  position: 'absolute',
                  bottom: '-25px',
                  left: '0',
                  fontSize: '14px',
                  color: '#6c757d'
                }}>
                  {`Found ${filteredUpcomingPrograms.length + filteredPastPrograms.length} of ${upcomingPrograms.length + pastPrograms.length} programs`}
                </div>
              )}
            </div>

            {/* No view selector needed as we'll display both sections */}

            {searchQuery && (filteredUpcomingPrograms.length === 0 && filteredPastPrograms.length === 0) && (
              <div className="empty-state" style={{
                padding: '40px 20px',
                textAlign: 'center',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                margin: '20px 0'
              }}>
                <i className="fas fa-search" style={{ 
                  fontSize: '48px',
                  color: '#6c757d',
                  marginBottom: '15px',
                  opacity: '0.5'
                }}></i>
                <p style={{ fontSize: '18px', color: '#666' }}>
                  No programs found matching "{searchQuery}".
                </p>
                <button
                  onClick={clearSearch}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    marginTop: '15px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Clear Search
                </button>
              </div>
            )}
            
            {/* Upcoming Programs Section */}
            <h2 style={{
              textAlign: 'center',
              marginBottom: '15px',
              fontSize: '28px',
              fontWeight: '600',
              color: '#333',
              position: 'relative',
              paddingBottom: '15px'
            }}>
              UPCOMING PROGRAMS
              {searchQuery && ` - Search Results`}
              <span style={{
                display: 'block',
                width: '60px',
                height: '4px',
                backgroundColor: 'var(--primary-color)',
                margin: '15px auto 0',
                borderRadius: '2px'
              }}></span>
            </h2>

            {!searchQuery && (
              <>
                <p style={{
                  textAlign: 'center',
                  maxWidth: '800px',
                  margin: '0 auto 20px',
                  color: '#555',
                  fontSize: '16px',
                  lineHeight: '1.6'
                }}>
                  Our upcoming programs offer opportunities to enhance your knowledge and skills in valuation. 
                  Register early to secure your spot at these educational and networking events.
                </p>
                
                {/* Member benefits for upcoming programs */}
                {!isLoggedIn && (
                  <div style={{
                    backgroundColor: '#e8f4fd',
                    border: '1px solid #b8daff',
                    borderRadius: '8px',
                    padding: '15px',
                    margin: '0 auto 40px',
                    maxWidth: '800px',
                    textAlign: 'center'
                  }}>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '14px',
                      color: '#004085',
                      fontWeight: '500'
                    }}>
                      <i className="fas fa-info-circle" style={{marginRight: '8px'}}></i>
                      BVA members get special pricing and post-event access to session recordings & presentations
                    </p>
                  </div>
                )}
              </>
            )}
            
            {renderUpcomingPrograms()}
            
            {/* Section divider */}
            <div style={{
              margin: '60px 0',
              height: '1px',
              background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(0,0,0,0.1), rgba(255,255,255,0))'
            }}></div>
            
            {/* Past Programs Section */}
            <h2 style={{
              textAlign: 'center',
              marginBottom: '15px',
              fontSize: '28px',
              fontWeight: '600',
              color: '#333',
              position: 'relative',
              paddingBottom: '15px'
            }}>
              PAST PROGRAMS
              {searchQuery && ` - Search Results`}
              <span style={{
                display: 'block',
                width: '60px',
                height: '4px',
                backgroundColor: 'var(--primary-color)',
                margin: '15px auto 0',
                borderRadius: '2px'
              }}></span>
            </h2>

            {!searchQuery && (
              <>
                <p style={{
                  textAlign: 'center',
                  maxWidth: '800px',
                  margin: '0 auto 20px',
                  color: '#555',
                  fontSize: '16px',
                  lineHeight: '1.6'
                }}>
                  Explore our extensive archive of past programs. Access valuable resources and materials from 
                  these sessions to continue learning at your own pace.
                </p>
                
                {/* Member-only content notice for past programs */}
                {!isLoggedIn && (
                  <div style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '8px',
                    padding: '20px',
                    margin: '0 auto 40px',
                    maxWidth: '800px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px'
                  }}>
                    <div style={{
                      backgroundColor: '#ffc107',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <i className="fas fa-video" style={{ fontSize: '18px', color: '#333' }}></i>
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '16px', 
                        fontWeight: '600',
                        color: '#856404'
                      }}>
                        Session Videos & PPTs - Members Only
                      </h4>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '14px',
                        color: '#856404',
                        lineHeight: '1.5'
                      }}>
                        Past program recordings, presentations, and exclusive materials are available only to BVA members. 
                        <a href="/membership" style={{ 
                          color: '#0063B2', 
                          fontWeight: '600',
                          textDecoration: 'none',
                          marginLeft: '4px'
                        }}>Join today</a> to unlock access to our complete resource library.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {renderPastPrograms()}
            
            {/* Benefits section remains unchanged */}
            <div style={{ 
              marginTop: '60px',
              padding: '30px', 
              borderRadius: '12px',
              backgroundColor: '#f8f9fa',
              boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
            }}>
              <h2 style={{
                fontSize: '24px',
                marginBottom: '20px',
                color: '#333',
                textAlign: 'center',
                position: 'relative',
                paddingBottom: '10px'
              }}>
                PROGRAM BENEFITS
                <span style={{
                  display: 'block',
                  width: '50px',
                  height: '3px',
                  backgroundColor: 'var(--primary-color)',
                  margin: '10px auto 0',
                  borderRadius: '2px'
                }}></span>
              </h2>
              <p style={{
                textAlign: 'center',
                marginBottom: '25px',
                color: '#555',
                fontSize: '16px'
              }}>
                Our programs are designed to provide maximum value to valuation professionals. 
                By participating in BVA programs, you will:
              </p>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    backgroundColor: 'rgba(var(--primary-rgb), 0.1)',
                    color: 'var(--primary-color)',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '15px',
                    flexShrink: 0
                  }}>
                    <i className="fas fa-user-tie"></i>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '16px', marginBottom: '8px', color: '#333' }}>Expert Insights</h3>
                    <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                      Gain insights from industry experts and thought leaders
                    </p>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    backgroundColor: 'rgba(var(--primary-rgb), 0.1)',
                    color: 'var(--primary-color)',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '15px',
                    flexShrink: 0
                  }}>
                    <i className="fas fa-sync-alt"></i>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '16px', marginBottom: '8px', color: '#333' }}>Stay Updated</h3>
                    <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                      Keep up with the latest valuation methodologies and practices
                    </p>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    backgroundColor: 'rgba(var(--primary-rgb), 0.1)',
                    color: 'var(--primary-color)',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '15px',
                    flexShrink: 0
                  }}>
                    <i className="fas fa-network-wired"></i>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '16px', marginBottom: '8px', color: '#333' }}>Networking</h3>
                    <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                      Network with fellow professionals and expand your professional circle
                    </p>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  border: '2px solid #ffc107'
                }}>
                  <div style={{
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    color: '#ffc107',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '15px',
                    flexShrink: 0
                  }}>
                    <i className="fas fa-video"></i>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '16px', marginBottom: '8px', color: '#333' }}>
                      Exclusive Content Access
                      <br />
                      <span style={{
                        fontSize: '11px',
                        backgroundColor: '#ffc107',
                        color: '#333',
                        padding: '2px 6px',
                        borderRadius: '12px',
                        marginLeft: '8px',
                        fontWeight: 'bold'
                      }}>
                        MEMBERS ONLY
                      </span>
                    </h3>
                    <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                      Access session recordings, presentations, and downloadable resources
                    </p>
                  </div>
                </div>
              </div>
                
              <div style={{
                padding: '20px',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <p style={{
                  margin: '0',
                  fontSize: '16px',
                  fontWeight: '500'
                }}>
                  BVA members enjoy special pricing and exclusive access to session videos & PPTs.
                </p>
                <a href="/membership" style={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  marginLeft: '15px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.35)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.25)';
                }}>
                  <span>BECOME A MEMBER TODAY</span>
                  <i className="fas fa-arrow-right" style={{marginLeft: '8px'}}></i>
                </a>
              </div>
            </div>
          </>
        )}
      </section>
      
      {/* Show login modal if showLoginModal is true */}
      {showLoginModal && (
        <LoginModal 
          onClose={() => setShowLoginModal(false)}
          programId={selectedProgramId}
        />
      )}
      
      {/* Show payment result modal if showPaymentModal is true */}
      {showPaymentModal && (
        <PaymentResultModal />
      )}
      
      <Footer />
    </div>
  );
}

export default ProgramsPage;